import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import {
  useEditor,
  useFieldErrorTranslation,
} from "../../../../../common/editing/save-coordinator";
import { ThemedDateTimePicker } from "../../../../../common/input/DateTimeSelectors/ThemedDateTimePicker";

interface BidSubmissionDeadlineFieldProps {
  eventId: string;
  currentDeadline: string | null;
}

const FIELD_PATH = "eventData.bidSubmissionDeadline";
const END_OF_DAY = { hours: 23, minutes: 59 };

export const BidSubmissionDeadlineField = ({
  eventId,
  currentDeadline,
}: BidSubmissionDeadlineFieldProps) => {
  const { t } = useTranslation();
  const { updateField, fieldStatus, fieldError } = useEditor<
    SourcingEventV2Dto,
    SourcingEventV2PatchDto
  >(eventId);
  const translateFieldError = useFieldErrorTranslation();

  const error = fieldError(FIELD_PATH);
  const status = fieldStatus(FIELD_PATH);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handleChange = (iso?: string) => {
    updateField({ eventData: { bidSubmissionDeadline: iso ?? null } });
  };

  return (
    <FormControl isInvalid={Boolean(error)} isDisabled={status === "saving"}>
      <FormLabel htmlFor="bidSubmissionDeadline">{t("Bid deadline")}</FormLabel>
      <ThemedDateTimePicker
        id="bidSubmissionDeadline"
        date={currentDeadline}
        onChange={handleChange}
        defaultTime={END_OF_DAY}
      />
      {error ? (
        <FormErrorMessage>{translateFieldError(error)}</FormErrorMessage>
      ) : (
        <FormHelperText>
          {t("Tender deadline timezone: {{timezone}}", { timezone })}
        </FormHelperText>
      )}
    </FormControl>
  );
};
